import { useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState, useRef } from 'react'
import type { StreamChat } from 'stream-chat'

import { client } from 'src/lib/constants'
import { queries } from 'src/lib/query-keys'
import { useMyUserData, useMyUserTokens } from 'src/shared/features/users/api/accounts/use-my-user'

type ChatContextType = {
  client: StreamChat | null
  isConnecting: boolean
  reconnect: () => Promise<void>
}

export const ChatContext = createContext<ChatContextType>({
  client: null,
  isConnecting: true,
  reconnect: async () => {},
})

const isTemporaryId = (id: number) => id === 999999999

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [chatClient, setChatClient] = useState<StreamChat | null>(null)
  const [isConnecting, setIsConnecting] = useState(true)
  const { data: user } = useMyUserData()
  const { data: tokens, isLoading: isTokensLoading } = useMyUserTokens()
  const queryClient = useQueryClient()

  // Use refs to track connection state across re-renders and async operations
  const isConnectedRef = useRef(false)
  const currentUserIdRef = useRef<string | null>(null)

  // Public method to force reconnection
  const reconnect = async () => {
    if (!user?.id || !tokens?.streamChatToken || isTemporaryId(user.id)) {
      queryClient.invalidateQueries({ queryKey: queries.users.me._ctx.tokens.queryKey })
      queryClient.invalidateQueries({ queryKey: queries.users.me.queryKey })
      return
    }

    await initClient()
  }

  const initClient = async () => {
    setIsConnecting(true)
    if (!user?.id || !tokens?.streamChatToken || isTemporaryId(user?.id)) {
      setIsConnecting(false)
      return
    }

    // Don't reconnect if already connected with same user
    if (isConnectedRef.current && currentUserIdRef.current === user.id.toString()) {
      setIsConnecting(false)
      return
    }

    try {
      if (client) {
        // Disconnect previous user if exists
        if (isConnectedRef.current) {
          try {
            await client.disconnectUser()
          } catch (disconnectError) {
            // Error handling without logging
          }

          isConnectedRef.current = false
          currentUserIdRef.current = null
        }

        await client.connectUser(
          {
            id: user.id.toString(),
            name: user.fullName,
            image: user.profilePhoto,
          },
          tokens.streamChatToken,
        )

        isConnectedRef.current = true
        currentUserIdRef.current = user.id.toString()
        setChatClient(client)
      }
    } catch (error) {
      isConnectedRef.current = false
      currentUserIdRef.current = null
      setChatClient(null)
    } finally {
      setIsConnecting(false)
    }
  }

  useEffect(() => {
    if (!isTokensLoading) {
      initClient()
    }

    return () => {
      // Only disconnect if we're actually connected
      if (client && isConnectedRef.current) {
        try {
          client.disconnectUser()
        } catch (cleanupError) {
          // Error handling without logging
        }

        isConnectedRef.current = false
        currentUserIdRef.current = null
        setChatClient(null)
      }
    }
  }, [user?.id, tokens?.streamChatToken, isTokensLoading])

  return (
    <ChatContext.Provider
      value={{
        client: chatClient,
        isConnecting,
        reconnect,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export const useChatClient = () => {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error('useChatClient must be used within a ChatProvider')
  }
  return context
}
